import { useCallback, useSyncExternalStore } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useSession } from 'next-auth/react'

import { useCartRequest } from '@/hooks/useCartRequest'
import { useCurrentCart } from '@/hooks/useCurrentCart'

import type { Cart } from '@/gen/ecom/models'

/** Reads the cart `useCart` keeps in the query cache without adding a query observer: `null` when the visitor has
 * no cart, `undefined` while it is unknown. */
export const useCachedCart = (): Cart | null | undefined => {
  const queryClient = useQueryClient()
  const { status } = useSession()
  const { current: cartId, isCurrentCartUnknown } = useCurrentCart()
  const { cartCacheKey } = useCartRequest()

  const subscribe = useCallback(
    (onChange: () => void) => queryClient.getQueryCache().subscribe(onChange),
    [queryClient]
  )
  const cart = useSyncExternalStore(
    subscribe,
    () => queryClient.getQueryData<Cart>(cartCacheKey),
    () => undefined
  )

  const hasNoCart = status !== 'loading' && cartId == null && !isCurrentCartUnknown

  return hasNoCart ? null : cart
}
